import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUtils";
import { toLocalizedNumber } from "../../utils/localeUtils";
import { usePopup } from "../../context/PopupContext";
import {
  FaStar,
  FaRegStar,
  FaStarHalfAlt,
  FaPen,
  FaTrash,
  FaTimes,
  FaCheck,
  FaStore,
  FaSortAmountDown,
  FaFilter,
  FaCheckCircle,
} from "react-icons/fa";
import Pagination from "../../components/Pagination";
import "./MyReviews.css";

const PER_PAGE = 10;

const MyReviews = () => {
  const { t } = useTranslation();
  const { showToast, showConfirm } = usePopup();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editComment, setEditComment] = useState("");
  const [hoverRating, setHoverRating] = useState(0);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await api.get("/reviews/my");
      setReviews(res.data || []);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (review) => {
    setEditingId(review._id);
    setEditRating(review.rating);
    setEditComment(review.comment || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditRating(0);
    setEditComment("");
    setHoverRating(0);
  };

  const handleSaveEdit = async (reviewId) => {
    if (editRating < 1) return;
    setSaving(true);
    try {
      const res = await api.put(`/reviews/${reviewId}`, {
        rating: editRating,
        comment: editComment,
      });
      setReviews((prev) =>
        prev.map((r) => (r._id === reviewId ? { ...r, ...res.data } : r)),
      );
      setEditingId(null);
      showToast(t("myReviews.updateSuccess"), "success");
    } catch (err) {
      showToast(
        err.response?.data?.message || t("myReviews.updateError"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reviewId) => {
    const confirmed = await showConfirm(t("myReviews.deleteConfirm"));
    if (!confirmed) return;
    try {
      await api.delete(`/reviews/${reviewId}`);
      setReviews((prev) => prev.filter((r) => r._id !== reviewId));
      showToast(t("myReviews.deleteSuccess"), "success");
    } catch (err) {
      showToast(
        err.response?.data?.message || t("myReviews.deleteError"),
        "error",
      );
    }
  };

  const renderStars = (rating, interactive = false) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const activeRating = interactive ? hoverRating || editRating : rating;
      const props = interactive
        ? {
            onClick: () => setEditRating(i),
            onMouseEnter: () => setHoverRating(i),
            onMouseLeave: () => setHoverRating(0),
          }
        : {};
      const cls = `myr-star ${interactive ? "myr-star--interactive" : ""}`;
      if (i <= Math.floor(activeRating)) {
        stars.push(
          <FaStar key={i} className={`${cls} myr-star--filled`} {...props} />,
        );
      } else if (i === Math.ceil(activeRating) && activeRating % 1 >= 0.5) {
        stars.push(
          <FaStarHalfAlt
            key={i}
            className={`${cls} myr-star--filled`}
            {...props}
          />,
        );
      } else {
        stars.push(<FaRegStar key={i} className={cls} {...props} />);
      }
    }
    return stars;
  };

  const getProductImage = (review) => {
    return getImageUrl(review.product?.image || review.product?.images?.[0]);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return t("myReviews.today");
    if (diffDays === 1) return t("myReviews.yesterday");
    if (diffDays < 30) return t("myReviews.daysAgo", { days: diffDays });
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const avgRating =
    reviews.length > 0
      ? (
          reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        ).toFixed(1)
      : 0;

  const ratingCounts = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
  }));

  const filteredReviews = (
    filter === "all"
      ? reviews
      : reviews.filter((r) => r.rating === parseInt(filter))
  ).sort((a, b) => {
    if (sortBy === "newest")
      return new Date(b.createdAt) - new Date(a.createdAt);
    if (sortBy === "oldest")
      return new Date(a.createdAt) - new Date(b.createdAt);
    if (sortBy === "highest") return b.rating - a.rating;
    if (sortBy === "lowest") return a.rating - b.rating;
    return 0;
  });

  const totalPages = Math.ceil(filteredReviews.length / PER_PAGE);
  const pageReviews = filteredReviews.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [filter, sortBy]);

  const maxCount = Math.max(...ratingCounts.map((r) => r.count), 1);

  return (
    <div className="myr-page">
      <Header />

      <div className="myr-hero">
        <div className="myr-hero-inner">
          <div className="myr-hero-text">
            <h1 className="myr-hero-title">{t("myReviews.title")}</h1>
            <p className="myr-hero-sub">{t("myReviews.subtitle")}</p>
          </div>
          {!loading && reviews.length > 0 && (
            <div className="myr-hero-badge">
              <span className="myr-hero-badge-num">{reviews.length}</span>
              <span className="myr-hero-badge-label">
                {t("myReviews.totalReviews", { count: reviews.length })}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="myr-body">
        {loading ? (
          <div className="myr-loading">
            <div className="myr-loading-dots">
              <span />
              <span />
              <span />
            </div>
            <p>{t("myReviews.loading")}</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="myr-empty">
            <div className="myr-empty-icon-wrap">
              <FaStar className="myr-empty-icon" />
            </div>
            <h2 className="myr-empty-heading">{t("myReviews.noReviews")}</h2>
            <p className="myr-empty-text">{t("myReviews.noReviewsHint")}</p>
            <Link to="/orders" className="myr-empty-btn">
              {t("myReviews.browseOrders")}
            </Link>
          </div>
        ) : (
          <div className="myr-content">
            {/* Stats Panel */}
            <div className="myr-stats">
              <div className="myr-stats-left">
                <div className="myr-stats-avg">{avgRating}</div>
                <div className="myr-stats-stars">
                  {renderStars(parseFloat(avgRating))}
                </div>
                <div className="myr-stats-label">
                  {t("myReviews.averageRating")}
                </div>
              </div>
              <div className="myr-stats-divider" />
              <div className="myr-stats-right">
                {ratingCounts.map(({ star, count }) => (
                  <button
                    key={star}
                    className={`myr-dist-row ${filter === String(star) ? "myr-dist-row--active" : ""}`}
                    onClick={() =>
                      setFilter(filter === String(star) ? "all" : String(star))
                    }
                  >
                    <span className="myr-dist-num">
                      {toLocalizedNumber(star)}
                    </span>
                    <FaStar className="myr-dist-icon" />
                    <div className="myr-dist-track">
                      <div
                        className="myr-dist-fill"
                        style={{ width: `${(count / maxCount) * 100}%` }}
                      />
                    </div>
                    <span className="myr-dist-count">
                      {toLocalizedNumber(count)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Toolbar */}
            <div className="myr-toolbar">
              <div className="myr-toolbar-left">
                <FaFilter className="myr-toolbar-icon" />
                <span className="myr-toolbar-count">
                  {filteredReviews.length}{" "}
                  {filter !== "all" && (
                    <span className="myr-toolbar-filter-meta">
                      (
                      <FaStar className="myr-toolbar-inline-star" aria-hidden />{" "}
                      {toLocalizedNumber(filter)})
                    </span>
                  )}
                </span>
                {filter !== "all" && (
                  <button
                    className="myr-toolbar-clear"
                    onClick={() => setFilter("all")}
                  >
                    <FaTimes /> {t("myReviews.clearFilter")}
                  </button>
                )}
              </div>
              <div className="myr-toolbar-right">
                <FaSortAmountDown className="myr-toolbar-icon" />
                <select
                  className="myr-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="newest">{t("myReviews.sortNewest")}</option>
                  <option value="oldest">{t("myReviews.sortOldest")}</option>
                  <option value="highest">{t("myReviews.sortHighest")}</option>
                  <option value="lowest">{t("myReviews.sortLowest")}</option>
                </select>
              </div>
            </div>

            {/* Review Cards */}
            <div className="myr-list">
              {pageReviews.map((review) => {
                const isEditing = editingId === review._id;
                return (
                  <div
                    key={review._id}
                    className={`myr-card ${isEditing ? "myr-card--editing" : ""}`}
                  >
                    <div
                      className="myr-card-accent"
                      data-rating={review.rating}
                    />
                    <div className="myr-card-inner">
                      {/* Product Row */}
                      <div className="myr-card-top">
                        <Link
                          to={`/products/${review.product?._id}`}
                          className="myr-card-img-link"
                        >
                          <img
                            src={getProductImage(review)}
                            alt={review.product?.name || ""}
                            className="myr-card-img"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/images/deafultpic.png";
                            }}
                          />
                        </Link>
                        <div className="myr-card-meta">
                          <Link
                            to={`/products/${review.product?._id}`}
                            className="myr-card-product-name"
                          >
                            {review.product?.name ||
                              t("myReviews.unknownProduct")}
                          </Link>
                          {review.farmer && (
                            <Link
                              to={`/farmer/${review.farmer._id}`}
                              className="myr-card-farmer"
                            >
                              <FaStore /> {review.farmer.name}
                            </Link>
                          )}
                          <div className="myr-card-meta-bottom">
                            <span className="myr-card-date">
                              {formatDate(review.createdAt)}
                            </span>
                            {review.isVerified && (
                              <span className="myr-card-verified">
                                <FaCheckCircle />{" "}
                                {t("myReviews.verifiedPurchase")}
                              </span>
                            )}
                          </div>
                        </div>
                        {!isEditing && (
                          <div
                            className="myr-card-rating-badge"
                            data-rating={review.rating}
                          >
                            <FaStar /> {review.rating}.0
                          </div>
                        )}
                      </div>

                      {/* Edit or View */}
                      {isEditing ? (
                        <div className="myr-edit">
                          <label className="myr-edit-label">
                            {t("myReviews.yourRating")}
                          </label>
                          <div className="myr-edit-stars">
                            {renderStars(editRating, true)}
                          </div>
                          <label className="myr-edit-label">
                            {t("myReviews.yourComment")}
                          </label>
                          <textarea
                            className="myr-edit-textarea"
                            value={editComment}
                            onChange={(e) => setEditComment(e.target.value)}
                            placeholder={t("myReviews.commentPlaceholder")}
                            rows={3}
                          />
                          <div className="myr-edit-actions">
                            <button
                              className="myr-btn myr-btn--ghost"
                              onClick={handleCancelEdit}
                              disabled={saving}
                            >
                              <FaTimes /> {t("common.cancel")}
                            </button>
                            <button
                              className="myr-btn myr-btn--primary"
                              onClick={() => handleSaveEdit(review._id)}
                              disabled={saving || editRating < 1}
                            >
                              <FaCheck />{" "}
                              {saving
                                ? t("myReviews.saving")
                                : t("myReviews.save")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="myr-card-body">
                          <div className="myr-card-stars">
                            {renderStars(review.rating)}
                          </div>
                          {review.comment && (
                            <p className="myr-card-comment">{review.comment}</p>
                          )}
                          <div className="myr-card-actions">
                            <button
                              className="myr-btn myr-btn--subtle"
                              onClick={() => handleEdit(review)}
                            >
                              <FaPen /> {t("myReviews.edit")}
                            </button>
                            <button
                              className="myr-btn myr-btn--danger"
                              onClick={() => handleDelete(review._id)}
                            >
                              <FaTrash /> {t("myReviews.delete")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MyReviews;
